import { useEffect, useState } from 'react';
import { Modal, Table, Tag, message, Empty, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getDesignDocumentLogs } from '@/api/requirements';
import { formatDate } from '@/utils';

const { Text } = Typography;

interface OperationLog {
  id: number;
  action: string;
  action_display: string;
  operator: { username: string; display_name: string } | null;
  operator_type: string;
  operator_type_display: string;
  before_data: Record<string, any> | null;
  after_data: Record<string, any> | null;
  description: string;
  ip_address: string | null;
  created_at: string;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  projectId: number;
}

const FIELD_LABELS: Record<string, string> = {
  file_description: '文件描述',
  features: 'Features',
  requirement_attribute: '需求属性',
  user_version: '版本',
  remarks: '备注',
};

const ACTION_COLORS: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
};

function formatFileSize(bytes: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function truncate(str: string, len = 40): string {
  return str && str.length > len ? str.slice(0, len) + '…' : str;
}

const renderChanges = (record: OperationLog) => {
  const { action, before_data, after_data, description } = record;

  // 附件上传
  if (after_data?.action === 'upload_attachment') {
    return (
      <div>
        <div>{description}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          所属文档：[{after_data.file_number}] {truncate(after_data.file_description ?? '')}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          文件：{after_data.file_name}（{formatFileSize(after_data.file_size)}）
        </Text>
      </div>
    );
  }

  // 附件删除
  if (before_data?.action === 'delete_attachment') {
    return (
      <div>
        <div>{description}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          所属文档：[{before_data.file_number}] {truncate(before_data.file_description ?? '')}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          文件：{before_data.file_name}（{formatFileSize(before_data.file_size)}）
        </Text>
      </div>
    );
  }

  // 文档新建
  if (action === 'create' && after_data) {
    const items = Object.entries(FIELD_LABELS)
      .filter(([k]) => after_data[k])
      .map(([k, label]) => (
        <span key={k} style={{ marginRight: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{label}：</Text>
          <Text style={{ fontSize: 12 }}>{after_data[k]}</Text>
        </span>
      ));
    return (
      <div>
        <div>{description}</div>
        <div style={{ marginTop: 2 }}>{items}</div>
      </div>
    );
  }

  // 文档删除
  if (action === 'delete' && before_data) {
    return (
      <div>
        <div>{description}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          [{before_data.file_number}] {truncate(before_data.file_description ?? '')}
          {before_data.features ? `（${before_data.features}）` : ''}
        </Text>
      </div>
    );
  }

  // 文档编辑
  if (action === 'update' && before_data && after_data) {
    const diffs = Object.entries(FIELD_LABELS).filter(
      ([k]) => JSON.stringify(before_data[k]) !== JSON.stringify(after_data[k])
    );
    if (diffs.length === 0) return <div>{description}</div>;
    return (
      <div>
        <div>{description}</div>
        <table style={{ marginTop: 4, fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>字段</th>
              <th style={thStyle}>修改前</th>
              <th style={thStyle}>修改后</th>
            </tr>
          </thead>
          <tbody>
            {diffs.map(([k, label]) => (
              <tr key={k}>
                <td style={tdStyle}>{label}</td>
                <td style={{ ...tdStyle, color: '#cf1322' }}>{before_data[k] || '—'}</td>
                <td style={{ ...tdStyle, color: '#389e0d' }}>{after_data[k] || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return <div>{description}</div>;
};

const thStyle: React.CSSProperties = {
  padding: '2px 10px',
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  fontWeight: 500,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '2px 10px',
  border: '1px solid #f0f0f0',
  maxWidth: 240,
  wordBreak: 'break-all',
};

const columns: ColumnsType<OperationLog> = [
  {
    title: '操作时间',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 170,
    render: (v) => formatDate(v),
  },
  {
    title: '操作类型',
    dataIndex: 'action',
    key: 'action',
    width: 90,
    render: (action, record) => (
      <Tag color={ACTION_COLORS[action] || 'default'}>{record.action_display}</Tag>
    ),
  },
  {
    title: '操作人',
    key: 'operator',
    width: 140,
    render: (_, record) => {
      if (!record.operator) return <Text type="secondary">系统</Text>;
      return (
        <div>
          <div>{record.operator.display_name || record.operator.username}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.operator_type_display}</Text>
        </div>
      );
    },
  },
  {
    title: '操作详情',
    key: 'description',
    render: (_, record) => renderChanges(record),
  },
];

const DesignDocumentLogModal = ({ open, onCancel, projectId }: Props) => {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getDesignDocumentLogs(projectId)
      .then((data) => setLogs(Array.isArray(data) ? data : data.results ?? []))
      .catch(() => message.error('获取操作日志失败'))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  return (
    <Modal
      title={<span><ClockCircleOutlined style={{ marginRight: 8 }} />设计材料操作日志</span>}
      open={open}
      onCancel={onCancel}
      width={960}
      footer={null}
      destroyOnHidden
    >
      {!loading && logs.length === 0 ? (
        <Empty description="暂无操作记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showTotal: (total) => `共 ${total} 条记录` }}
        />
      )}
    </Modal>
  );
};

export default DesignDocumentLogModal;
